export class ScreenshotUploadError extends Error {
  constructor(failedIndexes, images) {
    const count = failedIndexes.length;
    super(count === 1 ? '1 chart did not upload.' : `${count} charts did not upload.`);
    this.name = 'ScreenshotUploadError';
    this.failedIndexes = failedIndexes;
    this.images = images;
  }
}

/**
 * Uploads every pasted/selected image (data URL) with `uploadOne(src, index)`, which
 * must resolve with the stored image's URL. Images that are already URLs are kept.
 *
 * Resolves with the final URL list (same order). When any upload fails it rejects with
 * a ScreenshotUploadError whose `images` holds the uploaded URLs plus the failed
 * originals, so the form can keep them and retry without uploading twice.
 */
export async function uploadScreenshotList(images, uploadOne) {
  const list = (Array.isArray(images) ? images : []).filter((src) => typeof src === 'string' && src !== '');
  const results = await Promise.allSettled(list.map(async (src, index) => {
    if (!src.startsWith('data:')) return src;
    return uploadOne(src, index);
  }));

  const resolved = [];
  const failedIndexes = [];
  results.forEach((result, index) => {
    if (result.status === 'fulfilled' && typeof result.value === 'string' && result.value) {
      resolved.push(result.value);
      return;
    }
    if (result.status === 'rejected') {
      console.error('Chart upload failed:', result.reason?.code || result.reason?.message || 'unknown error');
    }
    resolved.push(list[index]);
    failedIndexes.push(index);
  });

  if (failedIndexes.length > 0) throw new ScreenshotUploadError(failedIndexes, resolved);
  return resolved;
}
